import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { 
  Phone, 
  Clock, 
  FileText, 
  Users, 
  AlertTriangle,
  TrendingDown,
  Frown,
  Calendar
} from 'lucide-react';

const problems = [
  {
    icon: Clock,
    title: 'Journees sur les chantiers',
    description: 'Tu passes tes journées a travailler, sans temps pour gérer ton entreprise.',
    color: 'bg-red-50 text-red-600 border-red-100',
  },
  {
    icon: Phone,
    title: 'Appels et messages non-stop',
    description: 'Tu réponds aux appels entre deux coups de marteau, au risque de rater des opportunités.',
    color: 'bg-orange-50 text-orange-600 border-orange-100',
  },
  {
    icon: FileText,
    title: 'Devis le soir',
    description: 'Tu fais tes devis tard le soir, fatigué, alors que le client attend depuis des jours.',
    color: 'bg-amber-50 text-amber-600 border-amber-100',
  },
  {
    icon: Calendar,
    title: 'Trésorerie tendue',
    description: 'Les factures s\'accumulent, pas le temps de relancer, les paiements tardent.',
    color: 'bg-purple-50 text-purple-600 border-purple-100',
  },
  {
    icon: Users,
    title: 'Recherche de clients',
    description: 'Pas le temps de prospecter, tu dépends du bouche-à-oreille.',
    color: 'bg-blue-50 text-blue-600 border-blue-100',
  },
  {
    icon: Frown,
    title: 'Stress permanent',
    description: 'Tu gères tout seul : technique, commercial, administratif. C\'est épuisant.',
    color: 'bg-pink-50 text-pink-600 border-pink-100',
  },
];

const consequences = [
  { icon: TrendingDown, text: 'Opportunités perdues' },
  { icon: AlertTriangle, text: 'Trésorerie fragilisée' },
  { icon: Clock, text: 'Manque d\'organisation' },
  { icon: Frown, text: 'Charge mentale élevée' },
];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.2,
    },
  },
};

const cardVariants = {
  hidden: { 
    opacity: 0, 
    y: 40,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const headerVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

export default function ProblemSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      className="section-premium bg-slate-50"
      data-testid="problem-section"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Header with reveal */}
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={headerVariants}
          className="text-center mb-16 md:mb-20"
        >
          <motion.span 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-block px-4 py-2 bg-red-100 text-red-600 text-xs font-semibold uppercase tracking-wider rounded-full mb-6"
          >
            La realite du terrain
          </motion.span>
          <h2 className="heading-section mb-6">
            Le quotidien des artisans{' '}
            <motion.span 
              className="text-red-500"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              est epuisant
            </motion.span>
          </h2>
          <p className="body-large max-w-3xl mx-auto">
            Plombiers, electriciens, macons, peintres... Tu es nombreux a vivre cette realite chaque jour.
          </p>
        </motion.div>

        {/* Problems Grid - Staggered */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-16 md:mb-20"
        >
          {problems.map((problem, index) => {
            const Icon = problem.icon;
            return (
              <motion.div
                key={index}
                variants={cardVariants}
                whileHover={{ 
                  y: -8, 
                  transition: { duration: 0.3, ease: "easeOut" } 
                }}
                className="card-premium bg-white rounded-2xl p-6 md:p-8 group cursor-default"
              >
                <motion.div 
                  className={`w-14 h-14 rounded-xl flex items-center justify-center mb-5 ${problem.color} border`}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  <Icon className="w-7 h-7" />
                </motion.div>
                <h3 className="heading-card mb-3 group-hover:text-ba-primary transition-colors">
                  {problem.title}
                </h3>
                <p className="text-slate-500 leading-relaxed">
                  {problem.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Consequences Block with scale-in */}
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ duration: 0.7, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="bg-gradient-to-r from-red-50 to-orange-50 rounded-3xl p-8 md:p-10 border border-red-100/50 gradient-animate"
        >
          <motion.h3 
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.7, duration: 0.5 }}
            className="text-xl md:text-2xl font-bold text-slate-800 mb-8 text-center"
          >
            Resultat : ton entreprise depend entierement de toi
          </motion.h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {consequences.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ 
                    delay: 0.8 + index * 0.1, 
                    duration: 0.4,
                    type: "spring",
                    stiffness: 200,
                  }}
                  whileHover={{ scale: 1.05 }}
                  className="flex flex-col items-center text-center group cursor-default"
                >
                  <motion.div 
                    className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-3 shadow-sm border border-red-100 group-hover:shadow-md transition-shadow"
                    whileHover={{ rotate: [0, -10, 10, 0] }}
                    transition={{ duration: 0.4 }}
                  >
                    <Icon className="w-6 h-6 text-red-500" />
                  </motion.div>
                  <span className="text-sm font-medium text-slate-600">{item.text}</span>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
